import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { convertBpToDecimal, formatNumberLocale } from "@/utils/helpers";
import { CompactFieldRow } from "./CompactFieldRow";
import Decimal from "decimal.js";

interface ShiftPricingCardProps {
  detail: any; // ServicePricingDetails
  additionalPricing?: any; // CarPurchasePriceDetails
  shiftMinutes: number;
}

/**
 * ShiftPricingCard component renders a single shift type's pricing breakdown.
 * Includes base pricing, car pricing, and purchase pricing if available.
 *
 * @component
 * @example
 * <ShiftPricingCard detail={pricingDetail} additionalPricing={carPurchaseDetail} shiftMinutes={480} />
 */
function ShiftPricingCard({ detail, additionalPricing, shiftMinutes }: ShiftPricingCardProps) {
  const { t } = useTranslation(["common", "components"]);
  const intl = useIntl();

  // Calculate margin percentages for car and purchase
  const calculateMarginPercent = (revenue: number, costs: number): number => {
    const revenueDec = new Decimal(revenue ?? 0);
    if (revenueDec.lte(0)) return 0;
    const costsDec = new Decimal(costs ?? 0);
    return revenueDec.minus(costsDec).div(revenueDec).mul(100).toNumber();
  };

  const carMarginPercent = calculateMarginPercent(
    Number(additionalPricing?.carRevenue ?? 0),
    Number(additionalPricing?.carCosts ?? 0)
  );

  const purchaseMarginPercent = calculateMarginPercent(
    Number(additionalPricing?.purchaseRevenue ?? 0),
    Number(additionalPricing?.purchaseCosts ?? 0)
  );

  // Base shift metrics
  const baseFields = [
    {
      key: "revenue",
      label: t("pricing.labels.revenue"),
      value: Number(detail.revenue),
      formatType: "currency" as const,
    },
    {
      key: "pricePerHour",
      label: t("pricing.labels.priceperhour"),
      value: Number(detail.pricePerHour),
      formatType: "currency" as const,
    },
    {
      key: "margin",
      label: t("pricing.labels.margin"),
      value: `${formatNumberLocale(intl, convertBpToDecimal(Number(detail.margin ?? 0)), "decimal", false, false, 0, 2)}%`,
      rawCopyValue: convertBpToDecimal(Number(detail.margin ?? 0)),
      formatType: "none" as const,
    },
    {
      key: "hours",
      label: t("pricing.labels.hours"),
      value: shiftMinutes / 60,
      formatType: "decimal" as const,
    },
  ];

  // Car section fields
  const carFields = additionalPricing ? [
    {
      key: "carCosts",
      label: t("components:pricedetails.labels.carcosts"),
      value: Number(additionalPricing.carCosts ?? 0),
      formatType: "currency" as const,
    },
    {
      key: "carRevenue",
      label: t("components:pricedetails.labels.carrevenue"),
      value: Number(additionalPricing.carRevenue ?? 0),
      formatType: "currency" as const,
    },
    {
      key: "carPricePerHour",
      label: t("components:pricedetails.labels.carpriceperhour"),
      value: Number(additionalPricing.carPricePerHour ?? 0),
      formatType: "currency" as const,
    },
    {
      key: "carMargin",
      label: t("components:pricedetails.labels.carmargin"),
      value: `${formatNumberLocale(intl, carMarginPercent, "decimal", false, false, 0, 2)}%`,
      rawCopyValue: carMarginPercent,
      formatType: "none" as const,
    },
  ] : [];

  // Purchase section fields
  const purchaseFields = additionalPricing ? [
    {
      key: "purchaseCosts",
      label: t("components:pricedetails.labels.purchasecosts"),
      value: Number(additionalPricing.purchaseCosts ?? 0),
      formatType: "currency" as const,
    },
    {
      key: "purchaseRevenue",
      label: t("components:pricedetails.labels.purchaserevenue"),
      value: Number(additionalPricing.purchaseRevenue ?? 0),
      formatType: "currency" as const,
    },
    {
      key: "purchasePricePerHour",
      label: t("components:pricedetails.labels.purchasepriceperhour"),
      value: Number(additionalPricing.purchasePricePerHour ?? 0),
      formatType: "currency" as const,
    },
    {
      key: "purchaseMargin",
      label: t("components:pricedetails.labels.purchasemargin"),
      value: `${formatNumberLocale(intl, purchaseMarginPercent, "decimal", false, false, 0, 2)}%`,
      rawCopyValue: purchaseMarginPercent,
      formatType: "none" as const,
    },
  ] : [];

  return (
    <Card className="space-y-1 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold">
          {t(`costdetailstype.${detail.type.toLowerCase()}`)}
        </div>
        <Badge variant="outline" className="text-[11px]">
          {t(`costdetailstype.${detail.type.toLowerCase()}`)}
        </Badge>
      </div>

      {/* Base shift metrics */}
      {baseFields.map((field) => (
        <CompactFieldRow
          key={field.key}
          label={field.label}
          value={field.value}
          intl={intl}
          formatType={field.formatType}
          rawCopyValue={field.rawCopyValue}
        />
      ))}

      {/* Car section */}
      {additionalPricing && (
        <>
          <Separator className="my-1.5" />
          <div className="space-y-1">
            <div className="text-xs font-semibold text-muted-foreground">
              {t("components:pricedetails.labels.cars")}
            </div>
            {carFields.map((field) => (
              <CompactFieldRow
                key={field.key}
                label={field.label}
                value={field.value}
                intl={intl}
                formatType={field.formatType}
                rawCopyValue={field.rawCopyValue}
              />
            ))}
          </div>

          {/* Purchase section */}
          <div className="space-y-1 pt-1">
            <div className="text-xs font-semibold text-muted-foreground">
              {t("components:pricedetails.labels.purchases")}
            </div>
            {purchaseFields.map((field) => (
              <CompactFieldRow
                key={field.key}
                label={field.label}
                value={field.value}
                intl={intl}
                formatType={field.formatType}
                rawCopyValue={field.rawCopyValue}
              />
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

export default ShiftPricingCard;
